import React from 'react';

import queryString from 'query-string';
import { useLocation } from 'react-router';

import { UiFilterTextField } from '../../../ui/ui-filter-text-field';
import { UiMultiselectFilter } from '../../../ui/ui-multiselect-filter';

import {
  ACCOUNT_STATUS_CONFIG,
  ACCOUNT_STATUS_ORDER,
  isAccountStatus,
  isAccountStatusArray,
} from '../../../util/account-status';
import { caseInsensitiveIncludes } from '../../../util/string-comparisons';

export type AccountQueryParams = {
  accountName: string;
  accountStatus: AccountStatus[];
};

type InputProps = {
  navigateWithQueryStringParams: (queryStringParams: AccountQueryParams) => void;
};

export const validateAccountQueryParams = (search: string): AccountQueryParams => {
  const params = queryString.parse(search);
  const { accountName, accountStatus } = params;
  const returnValue: AccountQueryParams = {
    accountName: '',
    accountStatus: [],
  };

  if (accountName && !Array.isArray(accountName)) {
    returnValue.accountName = accountName;
  }
  if (accountStatus && isAccountStatus(accountStatus)) {
    returnValue.accountStatus = [accountStatus];
  }
  if (accountStatus && isAccountStatusArray(accountStatus)) {
    returnValue.accountStatus = accountStatus;
  }

  return returnValue;
};

const hasOrgNameMatchingSearch = (filterByName: string, orgs: TeamEngineAccount['Organizations']) =>
  orgs.map((org) => caseInsensitiveIncludes(org.name, filterByName)).some(Boolean);
const hasAccountNameMatchingSearch = (filterByName: string, accountName: string) =>
  caseInsensitiveIncludes(accountName, filterByName);

const checkFilterByName = (queryParams: AccountQueryParams, account: TeamEngineAccount) =>
  hasAccountNameMatchingSearch(queryParams.accountName, account.accountName) ||
  hasOrgNameMatchingSearch(queryParams.accountName, account.Organizations);
const checkFilterByStatus = (queryParams: AccountQueryParams, account: TeamEngineAccount) =>
  queryParams.accountStatus && queryParams.accountStatus.length > 0
    ? queryParams.accountStatus.includes(account.status)
    : true;

export const filterAccountsByQueryParams = (
  queryParams: AccountQueryParams,
  accounts: TeamEngineAccount[],
) =>
  accounts.filter(
    (account) =>
      checkFilterByStatus(queryParams, account) && checkFilterByName(queryParams, account),
  );

export const AccountQueryParamFilters = ({ navigateWithQueryStringParams }: InputProps) => {
  const { search } = useLocation();
  const queryParams = validateAccountQueryParams(search);
  const { accountName, accountStatus } = queryParams;

  const handleFilterParamChange =
    (key: keyof AccountQueryParams) => (value: string | Array<SelectOption<string>>) => {
      navigateWithQueryStringParams({
        ...queryParams,
        [key]: Array.isArray(value) ? value.filter((v) => v.selected).map((v) => v.value) : value,
      });
    };

  const filterOptions: Array<SelectOption<string>> = ACCOUNT_STATUS_ORDER.map((aso) => ({
    label: ACCOUNT_STATUS_CONFIG[aso].text,
    value: aso,
    selected: accountStatus.length ? accountStatus.includes(aso) : true,
  }));

  return (
    <div className="u-display-flex u-flexWrap-wrap">
      <UiFilterTextField
        label="Name"
        className="u-margin-right"
        value={accountName}
        onChange={handleFilterParamChange('accountName')}
        autoFocus
      />
      <UiMultiselectFilter
        className="u-margin-right"
        label="Account Status"
        filterValueAndOptions={filterOptions}
        onFilterValueChange={handleFilterParamChange('accountStatus')}
        selectMinWidth={150}
      />
    </div>
  );
};
